import React from "react";
import { AppBar, Backdrop, Box, Button, CircularProgress, Container, Grid, IconButton, LinearProgress, Paper, TextField, Toolbar, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import useAuth from "@context/auth/AuthHook";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled, useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { fileManagerApi } from "@api/services/filemanager";
import { uploadFilesViaLambda } from "@api/services/lambda";
import AuditionService from "@modules/student/audition.service";

const Input = styled("input")({
  display: "none",
});

export default function ManualFixPage() {
  const { state } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [codigo, setCodigo] = React.useState<string>("");
  const [file, setFile] = React.useState<File | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = React.useState<number>(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!codigo || !file) {
      toast.warn("Por favor, preencha o código e selecione um arquivo MP4");
      return;
    }

    // Validação de arquivo MP4
    const isMp4 = file.type === "video/mp4" || file.name.toLowerCase().endsWith(".mp4");
    if (!isMp4) {
      toast.error("Formato inválido: selecione um arquivo .mp4");
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      // Busca todas as apresentações do RA e filtra pelo código
      // Busca apresentação apenas pelo código e sem_video=1
      const auditionService = new AuditionService(fileManagerApi);
      const response = await auditionService.getAll("", {
        code: codigo,
        sem_video: 1,
        _t: Date.now(),
      });
      console.log("Apresentações encontradas:", response.data);
      const presentation = response.data.data?.find((item: any) => item.code === codigo);
      if (!presentation) {
        console.error("Nenhuma apresentação encontrada para o código:", codigo);
        toast.error(`Apresentação com código "${codigo}" não encontrada.`);
        setLoading(false);
        return;
      }
      const presentationId = presentation.id;

      // 1. Faz upload do vídeo para S3 via Lambda
      // Nota: uploadFilesViaLambda com is_video_file=true usa /chrome/ por padrão
      // Então fazemos upload para /chrome/ e depois o sistema converte para /videos/
      await uploadFilesViaLambda(
        state.usuario,
        file,
        "video/mp4",
        codigo,
        true,
        "chrome",
        (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );

      // Aguarda processamento e conversão do vídeo
      await new Promise((resolve) => setTimeout(resolve, 5000));

      // 2. Atualiza o file_key no banco de dados para apontar para a versão convertida
      // O sistema espera o vídeo em /videos/ após a conversão
      const fileKey = `${process.env.REACT_APP_FILE_LOCATION}/videos/${codigo}.mp4`;
      
      // Atualiza apenas o file_key (file_url será regenerado automaticamente pelo backend)
      await new AuditionService(fileManagerApi).update(presentationId, {
        file_key: fileKey,
      });

      // 3. Força a regeneração da URL assinada fazendo uma nova requisição
      await fileManagerApi.get(`/auditions/${presentationId}`, {
        params: {
          activity: 1,
          user: 1,
          _t: Date.now(), // Cache buster
        },
      });

      toast.success("Vídeo corrigido e disponibilizado com sucesso! Atualizando lista...");
      setCodigo("");
      setFile(null);
      setUploadProgress(0);
      
      // Redireciona com timestamp para forçar remontagem do componente
      setTimeout(() => {
        window.location.href = `/?_refresh=${Date.now()}`;
      }, 1500);
    } catch (err: any) {
      const apiMsg = err?.response?.data?.message || err?.message || "Falha ao enviar a correção";
      toast.error(`Erro: ${apiMsg}`);
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Backdrop 
        open={loading} 
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}
      >
        <Paper 
          elevation={8}
          sx={{ 
            textAlign: 'center', 
            width: '450px', 
            maxWidth: '90%',
            p: 5,
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Efeito de fundo animado */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(45deg, transparent 30%, ${theme.palette.primary.main}10 50%, transparent 70%)`,
              animation: 'shimmer 2s infinite',
              '@keyframes shimmer': {
                '0%': { transform: 'translateX(-100%)' },
                '100%': { transform: 'translateX(100%)' }
              }
            }}
          />
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {/* Círculo de progresso com gradiente */}
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress 
                variant="determinate"
                value={100}
                size={100}
                thickness={3}
                sx={{ 
                  color: 'rgba(0, 0, 0, 0.1)',
                  position: 'absolute'
                }}
              />
              <CircularProgress 
                variant="determinate"
                value={uploadProgress}
                size={100}
                thickness={3}
                sx={{ 
                  color: theme.palette.primary.main,
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  }
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography 
                  variant="h5" 
                  component="div" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.primary.main
                  }}
                >
                  {Math.round(uploadProgress)}%
                </Typography>
              </Box>
            </Box>

            <Typography 
              variant="h5" 
              sx={{ 
                mt: 4, 
                mb: 1,
                fontWeight: 600,
                color: theme.palette.text.primary
              }}
            >
              Enviando arquivo
            </Typography>
            
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 3,
                color: theme.palette.text.secondary
              }}
            >
              Aguarde enquanto processamos seu vídeo...
            </Typography>

            {/* Barra de progresso linear estilizada */}
            <Box sx={{ width: '100%', mt: 3 }}>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress} 
                sx={{ 
                  height: 10, 
                  borderRadius: 5,
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 5,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                    boxShadow: `0 2px 8px ${theme.palette.primary.main}40`
                  }
                }} 
              />
            </Box>
          </Box>
        </Paper>
      </Backdrop>
      <AppBar 
        elevation={0} 
        position="static" 
        sx={{ 
          backgroundColor: theme.palette.primary.main 
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate("/")}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div">
            Conversão Manual
          </Typography>
        </Toolbar>
      </AppBar>
      <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
        {!(state?.user?.professor || state?.user?.moodle_admin) ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" gutterBottom>
              Acesso negado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esta página é restrita a usuários administradores (Suporte).
            </Typography>
          </Paper>
        ) : (
          <Box>
            <Typography variant="h5" component="h1" gutterBottom>
              Upload de Vídeo MP4
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Utilize este formulário para fazer o upload de um vídeo MP4 convertido externamente e associá-lo a um código de apresentação que falhou.
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={8} md={5} lg={5}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Código da Apresentação *
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Ex: XYZ-123"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value)}
                    fullWidth
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F3F3F5',
                        '& fieldset': { borderColor: 'transparent' },
                        '&:hover fieldset': { borderColor: 'transparent' },
                        '&.Mui-focused fieldset': { borderColor: 'transparent' },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={10} lg={9}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Arquivo de Vídeo (MP4) *
                  </Typography>
                  <label htmlFor="video-upload">
                    <Input id="video-upload" type="file" accept="video/mp4" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 4,
                        textAlign: "center",
                        cursor: "pointer",
                        borderStyle: "dashed",
                        borderColor: (theme) => theme.palette.grey[300],
                        borderRadius: 1,
                        minHeight: 160,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <CloudUploadIcon fontSize="large" />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {file ? file.name : "Arraste e solte seu arquivo MP4 aqui"}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        ou
                      </Typography>
                          <Button
                            component="span"
                            variant="contained"
                            sx={{
                              mt: 1,
                              backgroundColor: "#fff",
                              color: "inherit",
                              border: "1px solid #0000001A",
                              boxShadow: "none",
                              '&:hover': {
                                backgroundColor: "#fff",
                                border: "1px solid #0000001A",
                                boxShadow: "none",
                              },
                            }}
                          >
                            Selecionar Arquivo
                          </Button>
                    </Paper>
                  </label>
                </Grid>

                <Grid item xs={12} md={10} lg={9}>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? "Enviando…" : "Enviar Conversão"}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </Container>
    </>
  );
}
